<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import MobileMenuComponent from '@/components/MobileMenuComponent.vue'

const route = useRoute()

// Scroll logic
const scrollY = ref(0)
const isLandingPage = computed(() => route.path === '/')
const isScrolled = computed(() => scrollY.value >= 80)

function handleScroll() {
  scrollY.value = window.scrollY || document.documentElement.scrollTop || 0
}

onMounted(() => {
  handleScroll() // set initial state (important on refresh-at-scroll)
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

// Menu logic
const menuOpen = ref(false)

function toggleMenu() {
  menuOpen.value = !menuOpen.value
}
function closeMenu() {
  menuOpen.value = false
}

function handleResize() {
  if (window.innerWidth >= 800) {
    closeMenu()
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') closeMenu()
}

watch(() => route.path, closeMenu)
onMounted(() => {
  handleScroll()
  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('resize', handleResize)
})
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', handleResize)
})

//Component changes based on whether the background is transparent or not.
const primaryCtaClass = computed(() => {
  if (!isLandingPage.value || isScrolled.value || menuOpen.value) return 'border-landing-primary text-white bg-landing-primary hover:bg-landing-primary-hover'
  return 'bg-white text-landing-primary hover:bg-gray-100'
})

const secondaryCtaClass = computed(() => {
  if (!isLandingPage.value || isScrolled.value || menuOpen.value) return 'border-[#0A1835]/70 text-landing-primary bg-white hover:border-[#0A1835]'
  return 'bg-transparent text-white border border-gray-300 hover:border-white'
})

const textCtaClass = computed(() => {
  if (!isLandingPage.value || isScrolled.value || menuOpen.value) return 'hover:border-landing-primary'
  return 'hover:border-white'
})

const logoClass = computed(() => {
  if (!isLandingPage.value || isScrolled.value || menuOpen.value) return 'logo-landing-primary'
  return 'logo-white'
})

const navbarClass = computed(() => {
  if (!isLandingPage.value || menuOpen.value) return 'bg-white text-landing-primary shadow-md'
  return isScrolled.value
    ? 'bg-white text-landing-primary shadow-md'
    : 'bg-transparent text-white'
})

</script>

<template>
  <main class="leading-normal tracking-normal bg-gray-100" id="main-body">
    <nav class="bg-white height-full min-h-screen flex flex-col">
      <!-- NAVIGATION BAR -->
      <div :class="navbarClass" class="px-6 fixed z-50 w-full flex-col h-20 top-0">
        <!-- COMPONENTS -->
        <div class="items-center flex flex-row justify-between mb-0 pb-0 h-full">
          <!-- LOGO -->
          <RouterLink to="/">
            <div class="items-center w-32 min-w-32 max-w-32 mr-8 ">
            <svg class="fill-current" id="b" data-name="logo" xmlns="http://www.w3.org/2000/svg" viewBox="471.55 417.6 1801.27 417.6">
              <path
                d="M2258.759,672.108l-106.203-73.32v-132.108c0-10.014-4.411-19.436-12.103-25.848l-14.959-12.474c-8.319-6.938-18.867-10.758-29.7-10.758-4.815,0-9.583.747-14.17,2.22l-165.743,53.209c-28.632,9.192-47.869,35.572-47.869,65.643v127.095l6.358,12.117,16.037,11.073v40.165l6.36,12.118,118.431,81.753c11.567,7.985,25.112,12.206,39.17,12.206,7.16,0,14.249-1.11,21.07-3.3l64.658-20.756,6.133,4.233c6.109,4.217,13.262,6.446,20.685,6.446,3.782,0,7.527-.586,11.129-1.743l53.377-17.136c8.482-2.723,14.085-10.511,13.99-19.416v-6.196l4.769-1.531c13.512-4.338,22.597-16.786,22.606-30.976l.033-45.901c.007-10.708-5.249-20.732-14.06-26.816ZM2246.251,711.973l-15.13,4.858-91.659-63.276v-43.919l110.629,76.37c4.69,3.238,6.994,8.538,6.161,14.178-.836,5.639-4.572,10.046-10.001,11.79ZM2136.186,466.575v136.822l-19.118-13.197v-137.545l.022-.007c1.489-.479,3.012-.715,4.516-.715,3.041,0,6.02.96,8.571,2.822,3.821,2.784,6.009,7.092,6.009,11.82ZM1906.773,659.77l208.392-66.908,18.993,13.112-59.292,19.038-168.093,53.971v-19.213ZM1906.773,656.332v-97.815c0-22.935,14.67-43.053,36.507-50.063l170.512-54.748v136.156l-207.019,66.469ZM2059.961,633.235l76.225-24.474v44.457l-183.429,58.896-43.956-30.345,151.16-48.535ZM2137.556,656.217l89.635,61.876-156.759,50.333c-15.42,4.956-32.604,2.416-45.944-6.793l-68.332-47.172,181.401-58.244ZM1882.737,665.767v-127.095c0-23.559,15.214-44.421,37.645-51.622l165.743-53.209c3.168-1.017,6.43-1.515,9.669-1.515,7.309,0,14.502,2.532,20.27,7.342l12.521,10.442c-3.914-1.69-8.298-1.924-12.498-.577l-173.81,55.807c-23.194,7.446-38.78,28.818-38.78,53.179v121.517l-.067.022-20.693-14.289ZM2187.319,755.284l18.963,12.955-8.785,2.819-10.179-6.954v-8.819ZM2197.736,774.417h0s15.914-5.109,15.914-5.109l-24.285-16.593,10.989-3.533,27.056,18.679c.913.631,1.486,1.573,1.604,2.563l.033,13.238c.026,1.311-.65,3.068-2.633,3.703l-53.376,17.138c-5.872,1.89-12.427.923-17.519-2.589l-12.097-8.351h0s-15.186-10.483-15.186-10.483v-10.733l15.435-4.954,24.277,16.59,29.786-9.564ZM2245.679,761.78l-10.616,3.408-4.319,1.387c-.174-.214-.351-.423-.551-.617l.018-.018c-.283-.277-.598-.537-.94-.771l-28.381-19.596-16.847,5.417v14.841l9.508,6.496-25.073,8.05-24.278-16.59-19.238,6.172v14.837l15.566,10.745-69.593,22.341c-5.42,1.74-11.009,2.595-16.568,2.595-10.877,0-21.64-3.273-30.805-9.599l-118.431-81.753v-45.909l117.499,81.112c9.396,6.486,20.582,9.858,31.835,9.858,5.716,0,11.447-.871,16.969-2.642l175.819-56.453c4.921-1.58,8.791-5.103,10.835-9.645l-.029,39.371c-.006,7.744-5.01,14.598-12.381,16.965Z"
                fill="currentColor"></path>
              <g>
                <path
                  d="M637.32,747.375v-101.567h-98.024c-8.969,0-16.24,7.271-16.24,16.24v85.327h-51.511v-241.949h51.511v95.039h98.024c8.969,0,16.24-7.271,16.24-16.24v-78.799h51.871v241.949h-51.871Z"
                  fill="currentColor"></path>
                <path
                  d="M1554.231,747.375l-59.545-78.706c-7.028-9.29-20.867-9.638-28.354-.713h0c-2.739,3.265-4.24,7.391-4.24,11.653v67.765h-51.508v-241.949h51.508v108.098l85.608-108.098h63.481l-96.127,113.538,102.656,128.411h-63.478Z"
                  fill="currentColor"></path>
                <path
                  d="M1624.589,518.848c0-16.225,14.398-29.158,30.491-27.411,14.082,1.529,25.018,14.006,24.638,28.165-.399,14.884-12.575,26.813-27.559,26.813-14.873,0-27.569-12.333-27.569-27.567ZM1629.306,747.375v-158.965c0-8.969,7.271-16.24,16.24-16.24h29.829v175.205h-46.068Z"
                  fill="currentColor"></path>
                <path
                  d="M1719.26,703.846v-91.411h-29.018v-40.266h12.778c8.969,0,16.24-7.271,16.24-16.24v-31.643h46.431v47.882h35.549v40.266h-35.549v79.077c0,10.882,5.805,19.224,15.962,19.224,6.891,0,13.422-2.54,15.962-5.077l9.794,34.823c-6.894,6.165-19.227,11.245-38.452,11.245-32.283,0-49.696-16.687-49.696-47.882Z"
                  fill="currentColor"></path>
                <path
                  d="M806.178,567.809c-51.187,0-92.685,41.498-92.685,92.684s41.498,92.685,92.685,92.685,92.655-41.498,92.655-92.685-41.498-92.684-92.655-92.684ZM806.178,708.001c-24.751,0-44.814-21.602-44.814-48.221s20.063-48.251,44.814-48.251,44.784,21.602,44.784,48.251-20.063,48.221-44.784,48.221Z"
                  fill="currentColor"></path>
                <path
                  d="M1304.094,681.728c1.573-6.831,2.43-13.919,2.43-21.235,0-53.393-45.153-96.244-99.323-92.45-45.691,3.2-82.676,40.249-85.795,85.945-3.695,54.129,39.125,99.189,92.462,99.189,31.238,0,64.595-15.462,81.372-39.183l-30.762-17.594c-5.634-3.222-12.627-2.509-17.623,1.635-7.55,6.262-22.732,9.986-32.987,9.986-17.405,0-32.494-10.689-39.897-26.293h130.123ZM1170.284,648.49c4.715-21.206,22.435-36.954,43.584-36.954s38.841,15.747,43.556,36.954h-87.14Z"
                  fill="currentColor"></path>
                <path
                  d="M1014.732,567.809c-16.232,0-31.523,4.173-44.812,11.546v-7.174h-47.871v241.928h47.871v-48.496c0-10.78,10.265-18.412,20.675-15.61,7.701,2.072,15.795,3.175,24.137,3.175,51.158,0,92.657-41.498,92.657-92.685s-41.499-92.684-92.657-92.684ZM1014.732,708.001c-24.749,0-44.812-21.602-44.812-48.221s20.063-48.251,44.812-48.251,44.786,21.602,44.786,48.251-20.064,48.221-44.786,48.221Z"
                  fill="currentColor"></path>
              </g>
            </svg>
            </div>
          </RouterLink>

          <!-- NAVBAR LINKS -->
          <div class="fullNavBar">
            <!-- MOBILE VERSION??? -->
             <!-- HAMBURGER SVG -->
            <button
              @click="toggleMenu"
              :class="logoClass"
              class="md:hidden flex items-center px-3 py-2"
              :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
              :aria-expanded="menuOpen"
              aria-controls="mobile-menu"
              >
              <template v-if="!menuOpen">
                <!-- Hamburger Icon -->
                <svg 
                  class="fill-current h-6 w-6" 
                  fill="currentColor" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24">
                  <path 
                    fill="currentColor" 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                </svg>
              </template>
              <template v-else>
              <!-- X (Close) Icon -->
                <svg 
                  class="fill-current h-6 w-6" 
                  fill="currentColor" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24">
                  <path 
                    fill="currentColor" 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </template>
            </button>
            <!-- Mobile Component--> 
            <MobileMenuComponent v-if="menuOpen" @close="closeMenu" /> 
            <!-- TABLET/DESKTOP VERSION-->
            <!-- Desktop Nav CTAs-->
            <div class="hidden md:flex flex-row items-center space-x-3 h-full">
              <RouterLink 
                to="/home"
                :class="textCtaClass" class="flex font-medium items-center px-3 h-20 border-b-4 border-transparent mt-1 transition-all">
                Home
              </RouterLink>
              <RouterLink
                to="/about"
                :class="textCtaClass" class="flex font-medium items-center px-3 h-20 border-b-4 border-transparent mt-1 transition-all">
                About Us
              </RouterLink>
              <RouterLink
                to="/faq"
                :class="textCtaClass" class="flex font-medium items-center px-3 h-20 border-b-4 border-transparent mt-1 transition-all">
                FAQ
              </RouterLink>

              <RouterLink
                to="/contact"
                :class="textCtaClass" class="flex font-medium items-center px-3 h-20 border-b-4 border-transparent mt-1 transition-all">
                Contact
              </RouterLink>

              <div class="text-landing-primary transition-transform duration-200 hover:scale-105">
              <RouterLink to="/login">
                <Button :class="secondaryCtaClass" class="font-medium border rounded-full px-4 py-2">Login</Button>
              </RouterLink>
              </div>

              <div class="ml-20 box-border transition-transform duration-200 hover:scale-105">
              <RouterLink to="/signup">
                <Button :class="primaryCtaClass" class="font-medium border rounded-full px-4 py-2">Get Started</Button>
              </RouterLink>
              </div>

            </div>
          </div>
          <!-- END OF NAVBAR LINKS -->
        </div>
        <!-- END OF COMPONENTS -->
      </div>
      <!-- END OF NAVIGATION BAR WRAPPER-->
    

      <!-- VIEW OF REST OF THE PAGE -->
      <div class="page">
        <RouterView />
      </div>

    </nav>
  </main>
</template>

<style scoped>
.logo-white {
  color: white;
}
.logo-landing-primary {
  color: #0A1835;
}
</style>